import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/MasterclassPlayer.css';
import '../styles/MediaPlayerStyles.css';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { HeaderNavigation } from '../../shared/headerNavigation/HeaderNavigation';
import { initialLocationKey } from '../../shared/util/enum';
import { setSingleMedia } from '../mediaSlice';
import { Chapters } from './Chapters';
import { McMediaDescription } from './McMediaDescription';
import SingleMediaPlayer from './SingleMediaPlayer';

export function MasterclassPlayer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [chapterNo, setChapterNo] = useState(false);
  const [openCountdown, setOpenCountdown] = useState(false);
  const [keepPlaying, setKeepPlaying] = useState(false);

  const masterclass = useSelector((state) => state.media.masterclass);
  const nowActive = useSelector((state) => state.media.singleMedia);
  const chapterNumber = window.location.pathname.split('/')[4];

  // scroll top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (masterclass && chapterNumber !== chapterNo) {
      setChapterNo(chapterNumber);
      dispatch(setSingleMedia(masterclass.chapters[chapterNumber - 1]));
    }
  }, [chapterNumber, masterclass, chapterNo, dispatch]);

  useEffect(() => {
    if (nowActive?.chid) {
      const observer = new MutationObserver((mutationsList, mutationObserver) => {
        const mcMedia = document.getElementsByClassName('mcplayer');
        const text = document.getElementsByClassName('below-video');
        if (mcMedia[0]) {
          mcMedia[0].classList.add('fade-in');
          text[0].classList.add('fade-in');
          mutationObserver.disconnect(); // Stop observing when the classes are added
        }
      });

      // Start observing the document with the configured parameters
      observer.observe(document, { childList: true, subtree: true });
    }
  }, [nowActive?.chid]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('pageReload', true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleNavigation = () => {
    navigate(-1);
  };

  const handleAutoPlay = () => {
    if (chapterNumber < masterclass.chapters.length) {
      setOpenCountdown(true);
    }
  };

  // function called by autoplay
  const playNext = () => {
    const mcMedia = document.getElementsByClassName('mcplayer');
    const text = document.getElementsByClassName('below-video');
    if (mcMedia[0] && text[0]) {
      mcMedia[0].classList.remove('fade-in');
      text[0].classList.remove('fade-in');
    }
    setTimeout(() => {
      navigate(
        `/masterclass/${masterclass.chid}/${masterclass.title.replace(
          / /g,
          '_'
        )}/${Number(chapterNumber) + 1}/${masterclass.chapters[Number(chapterNumber)].title
          .replace(/ /g, '_')
          .toLowerCase()}`
      );
      setKeepPlaying(true);
    }, 700);
  };

  // setting player height, so other elements don't move around when chapters change
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [mediaHeight, setMediaHeight] = useState(0);

  const calculateHeight = useCallback(() => {
    let newValue;
    if (screenWidth < 850) {
      newValue = Math.floor(screenWidth * 0.6);
    } else {
      newValue = Math.floor(screenWidth * 0.38);
    }
    setMediaHeight(newValue);
  }, [screenWidth]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screenWidth]);

  useEffect(() => {
    calculateHeight();
  }, [screenWidth, calculateHeight]);

  return (
    <>
      <HeaderNavigation
        bottomBarActionButton={
          location.key !== initialLocationKey && {
            ariaLabel: t('nav.button.back'),
            onClick: handleNavigation,
            icon: {
              faIconDefinition: faChevronLeft,
              size: 'lg',
            },
            variant: 'icon-only',
            className: 'back-button',
          }
        }
      />
      <div id="masterclass-container">
        <div className="column-1">
          <div className="media-cont" style={{ height: `${mediaHeight}px` }}>
            <SingleMediaPlayer
              openCountdown={openCountdown}
              setOpenCountdown={setOpenCountdown}
              playNext={playNext}
              mediaHeight={mediaHeight}
              masterclass
              handleAutoPlay={handleAutoPlay}
              keepPlaying={keepPlaying}
              progress={masterclass.progress_array[chapterNumber - 1]}
            />
          </div>

          <div className="below-video">
            {nowActive && (
              <McMediaDescription
                masterclass={masterclass}
                nowActive={nowActive}
                chapterNo={chapterNo}
              />
            )}
          </div>
        </div>

        {nowActive && <Chapters masterclassPage masterclass={masterclass} />}
      </div>
    </>
  );
}
